import React from 'react';
import Table from 'components/grid/table';
import DateField from 'components/grid/table/components/fields/date-field';
import Form from 'components/form';
import { useTranslation } from 'i18n';
import TabbedOptions from 'routes/surface/components/node-modal-form/components/ris-search-modal/components/tabbed-options';
import TableScroll from 'components/grid/table/components/table-scroll';
import SearchInput from 'routes/surface/components/node-modal-form/components/ris-search-modal/components/search-input';
import { WithMargin } from 'components/layout';
import {
  ApiList,
  RightArrowCircleIcon,
  FieldLabel,
  FieldValue,
  Container,
  SearchContainer,
  TabContainer,
  HeaderControls,
  Header,
  FullScreen,
  BackButton,
} from './style';
import { risSearchQuery } from 'graphql/surface/queries/ris-search';
import { object, string } from 'yup';
import { RisTypeSearch } from 'graphql/types';
import { ModalContext } from 'hoc/with-modal';

export interface RisResultsTableProps {
  showRisNodeForm: Function;
  showSearchForm: Function;
  modal: ModalContext;
  filter: {
    search: string;
    type: string;
  };
  setFilter: Function;
}

function createValidationSchema(t) {
  return function () {
    return object().shape({
      search: string().required(t('Search term is required')),
      type: string().required(t('Search type is required')),
    });
  };
}

function RisResultsTable({
  showRisNodeForm,
  filter,
  setFilter,
  ...props
}: RisResultsTableProps) {
  const { t } = useTranslation();
  const searchInput = React.useRef<any>(null);
  const apiList = React.useRef<any>(null);

  const validationSchema = React.useMemo(createValidationSchema(t), []);

  React.useEffect(() => {
    setTimeout(() => searchInput.current.focus(), 200);
  }, []);

  function goToSearch() {
    props.showSearchForm();
  }

  function applySearchFilter(filter, { setSubmitting }) {
    filter.search = filter.search?.trim();
    setFilter(filter);
    setTimeout(() => setSubmitting(false), 2000);

    apiList.current.fetch({ filter });
  }

  return (
    <Form
      initialValues={filter}
      onSubmit={applySearchFilter}
      validationSchema={validationSchema}>
      {(form) => (
        <FullScreen>
          <Header>
            <BackButton onClick={goToSearch}>
              {t('Back to legal search')}
            </BackButton>
            <SearchContainer>
              <TabContainer>
                <WithMargin bottom="20px">
                  <TabbedOptions
                    name="type"
                    options={[
                      {
                        label: t('Federal Standards'),
                        value: RisTypeSearch.FederalStandards,
                      },
                      {
                        label: t('Judiciary'),
                        value: RisTypeSearch.Judicature,
                      },
                      {
                        label: t('Federal Law Gazette'),
                        value: RisTypeSearch.FederalLaw,
                      },
                    ]}
                  />
                </WithMargin>
                <SearchInput
                  ref={searchInput}
                  name="search"
                  clearSearch={() => form.setFieldValue('search', '')}
                  iconLeftClick={form.handleSubmit}
                />
              </TabContainer>
            </SearchContainer>
            <HeaderControls>
              <WithMargin right="15px">
                <Form.Button
                  label={t('Close')}
                  onClick={props.modal.hide}
                  secondary
                />
              </WithMargin>
              <Form.SubmitButton
                label={t('Search')}
                onClick={form.handleSubmit}
              />
            </HeaderControls>
          </Header>

          <Container>
            <ApiList
              ref={apiList}
              query={risSearchQuery}
              filter={filter}
              renderGridLayout={({ dataGrid }) => (
                <TableScroll>{dataGrid}</TableScroll>
              )}
              withRouter>
              <Table onRowClick={showRisNodeForm}>
                <Table.Field name="id">
                  {({ value }) => {
                    return (
                      <div>
                        <FieldLabel>{t('Paragraph')}</FieldLabel>
                        <FieldValue>{value}</FieldValue>
                      </div>
                    );
                  }}
                </Table.Field>
                <Table.Field name="type">
                  {({ value }) => {
                    return (
                      <div>
                        <FieldLabel>{t('Type')}</FieldLabel>
                        <FieldValue>{value}</FieldValue>
                      </div>
                    );
                  }}
                </Table.Field>
                <DateField name="date">
                  {({ value }) => {
                    return (
                      <div>
                        <FieldLabel>{t('Effective date')}</FieldLabel>
                        <FieldValue>{value}</FieldValue>
                      </div>
                    );
                  }}
                </DateField>
                <Table.Action
                  icon={<RightArrowCircleIcon />}
                  onClick={showRisNodeForm}
                />
              </Table>
              <ApiList.Pagination />
            </ApiList>
          </Container>
        </FullScreen>
      )}
    </Form>
  );
}

export default RisResultsTable;
